<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import draggable from 'vuedraggable';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { KANBAN_STAGE_COLORS as DEFAULT_COLORS } from 'dashboard/constants/kanbanColors';

const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const isLoading = ref(true);
const isSaving = ref(false);
const pipeline = ref(null);
const editingStageId = ref(null);
const showAddStage = ref(false);
const showColorPicker = ref(null);
const expandedDescription = ref(null);

const newStageName = ref('');
const newStageDescription = ref('');
const newStageColor = ref(DEFAULT_COLORS[0]);
const newStageWebhookEnabled = ref(false);
const newStageWebhookUrl = ref('');
const newStageDescriptionExpanded = ref(false);
const pipelineDescriptionExpanded = ref(false);

const pipelineId = computed(() => route.params.pipelineId);

const loadPipeline = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('pipelines/get');
    const found = getters['pipelines/getPipelineById'].value(pipelineId.value);
    if (found) {
      pipeline.value = JSON.parse(JSON.stringify(found));
    } else {
      useAlert(t('PIPELINES.EDIT.NOT_FOUND'));
      router.push({ name: 'pipelines_list' });
    }
  } catch (error) {
    useAlert(t('PIPELINES.EDIT.LOAD_ERROR'));
    router.push({ name: 'pipelines_list' });
  } finally {
    isLoading.value = false;
  }
};

const savePipeline = async () => {
  if (!pipeline.value) return;

  isSaving.value = true;
  try {
    await store.dispatch('pipelines/update', {
      id: pipeline.value.id,
      name: pipeline.value.name,
      description: pipeline.value.description,
    });
    useAlert(t('PIPELINES.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('PIPELINES.EDIT.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isSaving.value = false;
  }
};

const resetNewStageForm = () => {
  newStageName.value = '';
  newStageDescription.value = '';
  const stagesLength = pipeline.value?.stages?.length || 0;
  newStageColor.value = DEFAULT_COLORS[stagesLength % DEFAULT_COLORS.length];
  newStageWebhookEnabled.value = false;
  newStageWebhookUrl.value = '';
  newStageDescriptionExpanded.value = false;
};

const addStage = async () => {
  if (!newStageName.value.trim()) return;

  isSaving.value = true;
  try {
    const stage = await store.dispatch('pipelines/createStage', {
      pipelineId: pipeline.value.id,
      name: newStageName.value.trim(),
      description: newStageDescription.value.trim(),
      border_color: newStageColor.value,
      webhook_enabled: newStageWebhookEnabled.value,
      webhook_url: newStageWebhookUrl.value.trim(),
    });
    pipeline.value.stages = pipeline.value.stages || [];
    pipeline.value.stages.push(stage);
    resetNewStageForm();
    showAddStage.value = false;
    useAlert(t('PIPELINES.STAGES.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('PIPELINES.STAGES.ADD.ERROR');
    useAlert(errorMessage);
  } finally {
    isSaving.value = false;
  }
};

const updateStage = async stage => {
  isSaving.value = true;
  try {
    await store.dispatch('pipelines/updateStage', {
      pipelineId: pipeline.value.id,
      stageId: stage.id,
      name: stage.name,
      description: stage.description,
      border_color: stage.border_color,
      webhook_enabled: stage.webhook_enabled,
      webhook_url: stage.webhook_url,
    });
    editingStageId.value = null;
    useAlert(t('PIPELINES.STAGES.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('PIPELINES.STAGES.UPDATE.ERROR');
    useAlert(errorMessage);
  } finally {
    isSaving.value = false;
  }
};

const deleteStage = async stage => {
  if (pipeline.value.stages.length <= 1) {
    useAlert(t('PIPELINES.STAGES.DELETE.MIN_ERROR'));
    return;
  }

  isSaving.value = true;
  try {
    await store.dispatch('pipelines/deleteStage', {
      pipelineId: pipeline.value.id,
      stageId: stage.id,
    });
    pipeline.value.stages = pipeline.value.stages.filter(
      s => s.id !== stage.id
    );
    useAlert(t('PIPELINES.STAGES.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('PIPELINES.STAGES.DELETE.ERROR');
    useAlert(errorMessage);
  } finally {
    isSaving.value = false;
  }
};

const onDragEnd = async () => {
  isSaving.value = true;
  const stageIds = pipeline.value.stages.map(s => s.id);
  try {
    await store.dispatch('pipelines/reorderStages', {
      pipelineId: pipeline.value.id,
      stageIds,
    });
  } catch (error) {
    useAlert(t('PIPELINES.STAGES.REORDER.ERROR'));
    await loadPipeline();
  } finally {
    isSaving.value = false;
  }
};

const toggleColorPicker = stageId => {
  showColorPicker.value = showColorPicker.value === stageId ? null : stageId;
};

const setStageColor = (stage, color) => {
  stage.border_color = color;
  showColorPicker.value = null;
};

const toggleDescriptionExpand = stageId => {
  expandedDescription.value =
    expandedDescription.value === stageId ? null : stageId;
};

onMounted(loadPipeline);

watch(() => route.params.pipelineId, loadPipeline);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('PIPELINES.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="pipeline?.name || $t('PIPELINES.EDIT.TITLE')"
        :description="$t('PIPELINES.EDIT.DESCRIPTION')"
        feature-name="pipelines"
      >
        <template #actions>
          <Button
            slate
            :label="$t('PIPELINES.FORM.BACK')"
            icon="i-lucide-arrow-left"
            @click="router.push({ name: 'pipelines_list' })"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div v-if="pipeline" class="flex flex-col gap-6">
        <!-- Pipeline Info -->
        <div class="p-4 border rounded-lg border-n-weak bg-n-alpha-1">
          <h3 class="mb-4 text-sm font-semibold text-n-slate-12">
            {{ $t('PIPELINES.EDIT.INFO_TITLE') }}
          </h3>
          <div class="flex flex-col gap-4">
            <Input
              v-model="pipeline.name"
              :label="$t('PIPELINES.FORM.NAME.LABEL')"
              :placeholder="$t('PIPELINES.FORM.NAME.PLACEHOLDER')"
            />
            <Input
              :model-value="pipeline.key"
              :label="$t('PIPELINES.FORM.KEY.LABEL')"
              readonly
            />
            <div class="relative">
              <Textarea
                v-model="pipeline.description"
                :label="$t('PIPELINES.FORM.DESCRIPTION.LABEL')"
                :placeholder="$t('PIPELINES.FORM.DESCRIPTION.PLACEHOLDER')"
                :min-height="pipelineDescriptionExpanded ? '12rem' : '4rem'"
                :max-height="pipelineDescriptionExpanded ? '20rem' : '4rem'"
                auto-height
                resize
                custom-text-area-wrapper-class="transition-all duration-200"
              />
              <button
                type="button"
                class="absolute bottom-2 right-2 p-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded transition-colors z-10"
                @click.stop="
                  pipelineDescriptionExpanded = !pipelineDescriptionExpanded
                "
              >
                <i
                  :class="
                    pipelineDescriptionExpanded
                      ? 'i-lucide-minimize-2'
                      : 'i-lucide-maximize-2'
                  "
                  class="text-sm"
                />
              </button>
            </div>
            <div class="flex justify-end">
              <Button
                :label="$t('PIPELINES.FORM.SAVE')"
                :is-loading="isSaving"
                @click="savePipeline"
              />
            </div>
          </div>
        </div>

        <!-- Stages -->
        <div class="p-4 border rounded-lg border-n-weak bg-n-alpha-1">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('PIPELINES.STAGES.TITLE') }}
            </h3>
            <Button
              v-if="!showAddStage"
              icon="i-lucide-plus"
              xs
              :label="$t('PIPELINES.STAGES.ADD_BUTTON')"
              @click="showAddStage = true"
            />
          </div>

          <!-- Stages List -->
          <draggable
            v-model="pipeline.stages"
            item-key="id"
            handle=".drag-handle"
            class="flex flex-col gap-2"
            @end="onDragEnd"
          >
            <template #item="{ element: stage }">
              <div
                class="flex flex-col p-3 border rounded-lg border-n-weak bg-n-background"
              >
                <div class="flex items-center gap-3">
                  <!-- Drag Handle -->
                  <div
                    class="drag-handle cursor-grab text-n-slate-10 hover:text-n-slate-12"
                  >
                    <i class="i-lucide-grip-vertical" />
                  </div>

                  <!-- Color Indicator -->
                  <div class="relative">
                    <button
                      class="w-6 h-6 border-2 rounded-md cursor-pointer border-n-weak"
                      :style="{ backgroundColor: stage.border_color }"
                      @click="toggleColorPicker(stage.id)"
                    />
                    <!-- Color Picker Dropdown -->
                    <div
                      v-if="showColorPicker === stage.id"
                      class="absolute z-10 p-2 mt-1 border rounded-lg shadow-lg left-0 bg-n-background border-n-weak"
                    >
                      <div class="grid grid-cols-5 gap-1">
                        <button
                          v-for="color in DEFAULT_COLORS"
                          :key="color"
                          class="w-6 h-6 rounded-md cursor-pointer hover:scale-110 transition-transform"
                          :style="{ backgroundColor: color }"
                          @click="setStageColor(stage, color)"
                        />
                      </div>
                      <div class="mt-2">
                        <input
                          type="color"
                          :value="stage.border_color"
                          class="w-full h-8 cursor-pointer"
                          @input="stage.border_color = $event.target.value"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- Stage Name/Description -->
                  <div v-if="editingStageId !== stage.id" class="flex-1">
                    <div class="font-medium text-n-slate-12">
                      {{ stage.name }}
                    </div>
                    <div
                      v-if="stage.description"
                      class="text-xs text-n-slate-10"
                    >
                      {{ stage.description }}
                    </div>
                  </div>
                  <div v-else class="flex-1 flex flex-col gap-2">
                    <Input
                      v-model="stage.name"
                      :placeholder="$t('PIPELINES.STAGES.NAME_PLACEHOLDER')"
                      size="sm"
                    />
                    <div class="relative">
                      <Textarea
                        v-model="stage.description"
                        :placeholder="
                          $t('PIPELINES.STAGES.DESCRIPTION_PLACEHOLDER')
                        "
                        :min-height="
                          expandedDescription === stage.id ? '12rem' : '4rem'
                        "
                        :max-height="
                          expandedDescription === stage.id ? '20rem' : '4rem'
                        "
                        auto-height
                        resize
                        custom-text-area-wrapper-class="transition-all duration-200"
                      />
                      <button
                        type="button"
                        class="absolute bottom-2 right-2 p-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded transition-colors z-10"
                        @click.stop="toggleDescriptionExpand(stage.id)"
                      >
                        <i
                          :class="
                            expandedDescription === stage.id
                              ? 'i-lucide-minimize-2'
                              : 'i-lucide-maximize-2'
                          "
                          class="text-sm"
                        />
                      </button>
                    </div>
                  </div>

                  <!-- Webhook Indicator -->
                  <div
                    v-if="stage.webhook_enabled && stage.webhook_url"
                    class="flex items-center text-xs text-n-slate-10"
                  >
                    <i class="mr-1 i-lucide-webhook" />
                    {{ $t('PIPELINES.STAGES.WEBHOOK.INDICATOR') }}
                  </div>

                  <!-- Actions -->
                  <div class="flex gap-1">
                    <Button
                      v-if="editingStageId !== stage.id"
                      v-tooltip.top="$t('PIPELINES.STAGES.EDIT')"
                      icon="i-lucide-pen"
                      slate
                      xs
                      faded
                      @click="editingStageId = stage.id"
                    />
                    <template v-else>
                      <Button
                        v-tooltip.top="$t('PIPELINES.STAGES.SAVE')"
                        icon="i-lucide-check"
                        xs
                        :is-loading="isSaving"
                        @click="updateStage(stage)"
                      />
                      <Button
                        v-tooltip.top="$t('PIPELINES.STAGES.CANCEL')"
                        icon="i-lucide-x"
                        slate
                        xs
                        faded
                        @click="editingStageId = null"
                      />
                    </template>
                    <Button
                      v-tooltip.top="$t('PIPELINES.STAGES.DELETE')"
                      icon="i-lucide-trash-2"
                      ruby
                      xs
                      faded
                      :disabled="pipeline.stages.length <= 1"
                      @click="deleteStage(stage)"
                    />
                  </div>
                </div>

                <!-- Webhook Config (when editing) -->
                <div
                  v-if="editingStageId === stage.id"
                  class="flex flex-col gap-3 pt-3 mt-3 border-t border-n-weak"
                >
                  <div class="flex items-center gap-2">
                    <Switch v-model="stage.webhook_enabled" />
                    <span class="text-sm text-n-slate-12">
                      {{ $t('PIPELINES.STAGES.WEBHOOK.ENABLE') }}
                    </span>
                  </div>
                  <Input
                    v-if="stage.webhook_enabled"
                    v-model="stage.webhook_url"
                    :label="$t('PIPELINES.STAGES.WEBHOOK.URL_LABEL')"
                    :placeholder="
                      $t('PIPELINES.STAGES.WEBHOOK.URL_PLACEHOLDER')
                    "
                    type="url"
                  />
                  <p
                    v-if="stage.webhook_enabled"
                    class="text-xs text-n-slate-10"
                  >
                    {{ $t('PIPELINES.STAGES.WEBHOOK.HELP_TEXT') }}
                  </p>
                </div>
              </div>
            </template>
          </draggable>

          <!-- Add Stage Form -->
          <div
            v-if="showAddStage"
            class="p-4 mt-4 border-2 border-dashed rounded-lg border-n-weak"
          >
            <h4 class="mb-3 text-sm font-medium text-n-slate-12">
              {{ $t('PIPELINES.STAGES.ADD_TITLE') }}
            </h4>
            <div class="flex flex-col gap-3">
              <div class="flex items-center gap-3">
                <!-- Color Picker -->
                <div class="relative">
                  <button
                    class="w-8 h-8 border-2 rounded-md cursor-pointer border-n-weak"
                    :style="{ backgroundColor: newStageColor }"
                    @click="toggleColorPicker('new')"
                  />
                  <div
                    v-if="showColorPicker === 'new'"
                    class="absolute z-10 p-2 mt-1 border rounded-lg shadow-lg left-0 bg-n-background border-n-weak"
                  >
                    <div class="grid grid-cols-5 gap-1">
                      <button
                        v-for="color in DEFAULT_COLORS"
                        :key="color"
                        class="w-6 h-6 rounded-md cursor-pointer hover:scale-110 transition-transform"
                        :style="{ backgroundColor: color }"
                        @click="
                          newStageColor = color;
                          showColorPicker = null;
                        "
                      />
                    </div>
                    <div class="mt-2">
                      <input
                        v-model="newStageColor"
                        type="color"
                        class="w-full h-8 cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
                <Input
                  v-model="newStageName"
                  :placeholder="$t('PIPELINES.STAGES.NAME_PLACEHOLDER')"
                  class="flex-1"
                />
              </div>
              <div class="relative">
                <Textarea
                  v-model="newStageDescription"
                  :placeholder="$t('PIPELINES.STAGES.DESCRIPTION_PLACEHOLDER')"
                  :min-height="newStageDescriptionExpanded ? '12rem' : '4rem'"
                  :max-height="newStageDescriptionExpanded ? '20rem' : '4rem'"
                  auto-height
                  resize
                  custom-text-area-wrapper-class="transition-all duration-200"
                />
                <button
                  type="button"
                  class="absolute bottom-2 right-2 p-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded transition-colors z-10"
                  @click.stop="
                    newStageDescriptionExpanded = !newStageDescriptionExpanded
                  "
                >
                  <i
                    :class="
                      newStageDescriptionExpanded
                        ? 'i-lucide-minimize-2'
                        : 'i-lucide-maximize-2'
                    "
                    class="text-sm"
                  />
                </button>
              </div>
              <div class="flex items-center gap-2">
                <Switch v-model="newStageWebhookEnabled" />
                <span class="text-sm text-n-slate-12">
                  {{ $t('PIPELINES.STAGES.WEBHOOK.ENABLE') }}
                </span>
              </div>
              <Input
                v-if="newStageWebhookEnabled"
                v-model="newStageWebhookUrl"
                :label="$t('PIPELINES.STAGES.WEBHOOK.URL_LABEL')"
                :placeholder="$t('PIPELINES.STAGES.WEBHOOK.URL_PLACEHOLDER')"
                type="url"
              />
              <div class="flex justify-end gap-2">
                <Button
                  slate
                  :label="$t('PIPELINES.FORM.CANCEL')"
                  @click="
                    showAddStage = false;
                    resetNewStageForm();
                  "
                />
                <Button
                  :label="$t('PIPELINES.STAGES.ADD_BUTTON')"
                  :is-loading="isSaving"
                  :disabled="!newStageName.trim()"
                  @click="addStage"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
